import { useCallback, useEffect, useRef, useState } from 'react'
import { useSession } from '@/context/SessionContext'
import {
  createConversationV2,
  fetchConversationFull,
  insertUserMessage,
  sendChatMessage,
} from '@/lib/queries/chat'
import { parseSSEStream } from '@/lib/parseSSE'
import { captureException } from '@/lib/sentry'
import { VOICE_TO_TAB } from './data'

// Multi-voice fan-out state machine for chat-v2 — Slice 7 multi-turn shape.
//
// One "turn" = one user message + N parallel guide responses (+ optional
// synthesis). We accumulate turns in an array as the user sends; the column
// view stacks them vertically. Each turn is immutable once complete except
// when its primary or one of its voices is regenerated, which mutates that
// turn's voice slot.
//
// Reload-from-DB: loadConversation(id) fetches conversation + messages,
// groups assistant messages by metadata.voice into the corresponding turn,
// attaches metadata.is_synthesis rows as turn-level synthesis. The
// originating shell state (mode, anchor, voices, lenses_active, complexity,
// bible_version) lives on conversations.metadata and is restored by the
// caller (ChatDesktop / ChatMobile read this hook's `metadata` field).

// Strip persona-specific marker tags that aren't actual content. Spurgeon's
// system prompt uses `[ILLUSTRATION]` to flag illustration sections so the
// pastor can swap them out — we drop the literal bracket marker but keep
// surrounding prose. Other personas may add tags here over time.
function stripPersonaMarkers(text) {
  return text.replace(/\s*\[\/?ILLUSTRATION\]\s?/gi, ' ').replace(/  +/g, ' ')
}

// Extract `[FOLLOW_UPS]\n- ...\n[/FOLLOW_UPS]` from a streamed reply. The
// chat function appends this block to the AI's response as a plain-text
// list of suggested next prompts. Returns both the cleaned content (block
// stripped, persona markers removed) and the parsed list. Tolerant of an
// unclosed block during streaming so display stays stable.
export function parseFollowUps(text) {
  if (!text) return { cleanText: text || '', followUps: [] }
  const closed = text.match(/\[FOLLOW_UPS\]([\s\S]*?)\[\/FOLLOW_UPS\]/)
  if (closed) {
    const cleanText = stripPersonaMarkers(
      text.replace(/\s*\[FOLLOW_UPS\][\s\S]*?\[\/FOLLOW_UPS\]\s*$/, '').trim()
    )
    const followUps = closed[1]
      .split('\n')
      .map(line => line.replace(/^-\s*/, '').trim())
      .filter(Boolean)
    return { cleanText, followUps }
  }
  // Mid-stream: opener present but no closer yet — hide everything from the
  // opener onward so the [FOLLOW_UPS]\n- … fragment doesn't flash in the UI.
  const idx = text.indexOf('[FOLLOW_UPS]')
  if (idx >= 0) {
    return { cleanText: stripPersonaMarkers(text.slice(0, idx).trim()), followUps: [] }
  }
  return { cleanText: stripPersonaMarkers(text), followUps: [] }
}

function buildTurnsFromMessages(messages) {
  const turns = []
  let current = null
  for (const msg of messages || []) {
    if (msg.role === 'user') {
      const userMeta = msg.metadata || {}
      // Persisted voice_order + primary_voice (chat-v2 writes them on the
      // user row). Pre-seed turn.voices in the original display order so
      // reload doesn't reorder columns based on which assistant row
      // happened to land first. Legacy rows without these fields fall
      // through to the old "append in DB-row order" path below.
      const seededVoices = Array.isArray(userMeta.voice_order) ? userMeta.voice_order : []
      current = {
        id: msg.id,
        // userMessageId mirrors `id` for reloaded turns (which already
        // carry the DB user-message UUID). Live turns init this field
        // to null and have it filled in once insertUserMessage resolves —
        // edit-and-resend reads it as the truncate anchor (T3-B).
        userMessageId: msg.id,
        message: msg.content,
        voices: [...seededVoices],
        primaryId: typeof userMeta.primary_voice === 'string' ? userMeta.primary_voice : null,
        lenses: [],
        anchor: null,
        voiceState: {},
        synthesis: null,
        sentAt: msg.created_at ? new Date(msg.created_at).getTime() : Date.now(),
        attachments:
          Array.isArray(msg.attachments) && msg.attachments.length > 0 ? msg.attachments : null,
      }
      turns.push(current)
    } else if (msg.role === 'assistant' && current) {
      const meta = msg.metadata || {}
      if (meta.is_synthesis) {
        current.synthesis = {
          status: 'done',
          text: msg.content || '',
          error: null,
          messageId: msg.id,
        }
        continue
      }
      const voice = meta.voice
      if (!voice) continue
      if (!current.voices.includes(voice)) current.voices.push(voice)
      // Parse follow-ups out of the saved content so the chips reappear on
      // reload (the [FOLLOW_UPS] tail is persisted as part of the message).
      const { followUps } = parseFollowUps(msg.content || '')
      // Latest assistant message for a voice wins (regenerate replays).
      current.voiceState[voice] = {
        status: 'done',
        text: msg.content || '',
        usedLenses: Array.isArray(meta.lenses_used) ? meta.lenses_used : [],
        messageId: msg.id,
        followUps,
        stopReason: null,
        disputedTopic: typeof meta.disputed_topic === 'string' ? meta.disputed_topic : null,
      }
    }
  }
  // primaryId fallback — turns whose user row didn't carry primary_voice
  // (legacy) default to the first voice that landed. Same fallback if
  // primary_voice referenced a voice that never produced a reply (rare,
  // but possible mid-stream abort).
  turns.forEach(t => {
    if (!t.primaryId || !t.voices.includes(t.primaryId)) {
      t.primaryId = t.voices[0] ?? null
    }
  })
  return turns
}

export default function useVoiceFanOut() {
  const { user } = useSession()
  const [turns, setTurns] = useState([])
  const [conversationId, setConversationId] = useState(null)
  const [conversationMetadata, setConversationMetadata] = useState({})
  // Monthly cap tracker — chat function emits `monthlyMessagesUsed` /
  // `monthlyMessagesRemaining` in the meta event for free users (premium
  // / admin / beta get null). MessageQuotaTicker renders from these.
  const [monthlyMessagesUsed, setMonthlyMessagesUsed] = useState(null)
  const [monthlyMessagesRemaining, setMonthlyMessagesRemaining] = useState(null)
  // Aborts are keyed by `${turnId}:${voiceId}` so a new turn doesn't
  // accidentally cancel a stream we're keeping for an earlier turn (we
  // don't actually keep them, but the namespacing is cheap insurance).
  const abortRefs = useRef({})

  // SSE delta batching — text events arrive ~30/s/voice, ×4 voices that's
  // ~120 setTurns/sec at the top of the tree. We coalesce every chunk into
  // a per-(turn,voice) buffer and flush once per animation frame so the
  // reconciler runs at ~60Hz max regardless of stream rate.
  const pendingTextRef = useRef(new Map())
  const rafIdRef = useRef(null)

  const flushPendingText = useCallback(() => {
    rafIdRef.current = null
    const pending = pendingTextRef.current
    if (pending.size === 0) return
    const snapshot = new Map(pending)
    pending.clear()
    setTurns(prev =>
      prev.map(t => {
        let nextVoiceState = null
        for (const [key, delta] of snapshot) {
          const sep = key.indexOf(':')
          const tId = key.slice(0, sep)
          const vId = key.slice(sep + 1)
          if (tId !== t.id) continue
          const cur = (nextVoiceState ?? t.voiceState)[vId]
          if (!cur) continue
          if (!nextVoiceState) nextVoiceState = { ...t.voiceState }
          nextVoiceState[vId] = { ...cur, text: cur.text + delta }
        }
        return nextVoiceState ? { ...t, voiceState: nextVoiceState } : t
      })
    )
  }, [])

  const enqueueText = useCallback(
    (turnId, voiceId, delta) => {
      const key = `${turnId}:${voiceId}`
      const cur = pendingTextRef.current.get(key) || ''
      pendingTextRef.current.set(key, cur + delta)
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(flushPendingText)
      }
    },
    [flushPendingText]
  )

  const cancelAll = useCallback(() => {
    Object.values(abortRefs.current).forEach(ac => ac.abort())
    abortRefs.current = {}
    pendingTextRef.current.clear()
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
  }, [])

  useEffect(() => () => cancelAll(), [cancelAll])

  // Patch a single voice slot in a specific turn.
  const updateVoice = useCallback((turnId, voiceId, patch) => {
    setTurns(prev =>
      prev.map(t => {
        if (t.id !== turnId) return t
        const cur = t.voiceState[voiceId]
        if (!cur) return t
        return {
          ...t,
          voiceState: { ...t.voiceState, [voiceId]: { ...cur, ...patch } },
        }
      })
    )
  }, [])

  const streamOne = useCallback(
    async ({
      turnId,
      voiceId,
      isPrimary,
      conversationId: convId,
      message,
      lenses,
      anchor,
      attachments,
      continueFromMessageId = null,
      userMessageId = null,
    }) => {
      const ac = new AbortController()
      const refKey = `${turnId}:${voiceId}`
      abortRefs.current[refKey] = ac
      try {
        const res = await sendChatMessage(
          {
            message,
            conversationId: convId,
            tab: VOICE_TO_TAB[voiceId],
            voiceId,
            skipUserInsert: true,
            skipAutoRoute: true,
            skipTitleGen: !isPrimary,
            lenses: lenses?.length ? lenses : null,
            anchor: anchor || null,
            // T2-B: send the multimodal blocks to every selected voice,
            // not just primary. Cost is N×image-tokens but multi-voice is
            // opt-in (advanced complexity), and the secondary voices were
            // previously answering "blind" to imagery the user actually
            // sent. Persistence lives on the user row (set in startTurn);
            // each voice's request still carries the per-call signed URL
            // payload so Claude can fetch the bytes.
            attachments: attachments?.length ? attachments : [],
            // T2-A: pass the pre-inserted user-message id so the edge
            // function can filter it out of history before appending the
            // new user turn (otherwise the prompt would be duplicated).
            ...(userMessageId ? { userMessageId } : {}),
            // Continue-generating: when set, the chat function appends new
            // tokens to the existing assistant row instead of inserting a
            // fresh one. Used by continueGenerating(voiceId) below.
            ...(continueFromMessageId ? { continueFromMessageId } : {}),
          },
          { signal: ac.signal }
        )

        await parseSSEStream(res, evt => {
          if (evt.type === 'text' && typeof evt.text === 'string') {
            enqueueText(turnId, voiceId, evt.text)
          } else if (evt.type === 'done') {
            // Commit any buffered text before flipping status so the final
            // chunk doesn't land after the status change. Then parse the
            // [FOLLOW_UPS] tail off the accumulated text into a proper
            // followUps array on the voice slot.
            flushPendingText()
            setTurns(prev =>
              prev.map(t => {
                if (t.id !== turnId) return t
                const cur = t.voiceState[voiceId]
                if (!cur) return t
                const { followUps } = parseFollowUps(cur.text || '')
                return {
                  ...t,
                  voiceState: {
                    ...t.voiceState,
                    [voiceId]: { ...cur, status: 'done', followUps },
                  },
                }
              })
            )
          } else if (evt.type === 'stop_reason' && typeof evt.stop_reason === 'string') {
            // Claude stops with `max_tokens` when it hits the response cap
            // mid-thought. Surfacing this lets us show a "Continue" button
            // on the affected voice's reply.
            updateVoice(turnId, voiceId, { stopReason: evt.stop_reason })
          } else if (evt.type === 'error') {
            flushPendingText()
            // If the response already streamed (and the assistant row is
            // saved server-side), treat a late post-stream error as cosmetic
            // — don't blow away the user's visible answer. We still log it
            // so a real failure isn't silently swallowed.
            const sseErr = new Error(`SSE error event: ${String(evt.error || 'error')}`)
            captureException(sseErr, {
              tags: { feature: 'chat', op: 'stream-sse-error', voice: voiceId },
            })
            setTurns(prev =>
              prev.map(t => {
                if (t.id !== turnId) return t
                const cur = t.voiceState[voiceId]
                if (!cur) return t
                if (cur.text && cur.text.length > 20) {
                  console.warn(
                    '[chat-v2] late stream error after text — preserving answer:',
                    evt.error
                  )
                  return {
                    ...t,
                    voiceState: { ...t.voiceState, [voiceId]: { ...cur, status: 'done' } },
                  }
                }
                return {
                  ...t,
                  voiceState: {
                    ...t.voiceState,
                    [voiceId]: { ...cur, status: 'error', error: String(evt.error || 'error') },
                  },
                }
              })
            )
          } else if (evt.type === 'meta') {
            const patch = {}
            if (Array.isArray(evt.lenses_used)) patch.usedLenses = evt.lenses_used
            if (typeof evt.disputed_topic === 'string') {
              patch.disputedTopic = evt.disputed_topic

              console.info(
                '[chat-v2] disputed_topic from server:',
                evt.disputed_topic,
                'voice:',
                voiceId
              )
            }
            if (Object.keys(patch).length > 0) updateVoice(turnId, voiceId, patch)
            // Capture quota counts at the hook level — they're per-user
            // not per-voice, so any voice's meta event is fine. Multi-voice
            // turns will fire this N times but the value is identical.
            if (typeof evt.monthlyMessagesUsed === 'number') {
              setMonthlyMessagesUsed(evt.monthlyMessagesUsed)
            }
            if (typeof evt.monthlyMessagesRemaining === 'number') {
              setMonthlyMessagesRemaining(evt.monthlyMessagesRemaining)
            }
          } else if (evt.type === 'assistant_message_id' && typeof evt.id === 'string') {
            updateVoice(turnId, voiceId, { messageId: evt.id })
          }
        })
        // Stream ended without an explicit done — promote to done if still streaming.
        flushPendingText()
        setTurns(prev =>
          prev.map(t => {
            if (t.id !== turnId) return t
            const cur = t.voiceState[voiceId]
            if (!cur || cur.status !== 'streaming') return t
            return {
              ...t,
              voiceState: { ...t.voiceState, [voiceId]: { ...cur, status: 'done' } },
            }
          })
        )
      } catch (err) {
        if (err?.name === 'AbortError') return
        captureException(err, {
          tags: { feature: 'chat', op: 'stream-voice', voice: voiceId },
        })
        updateVoice(turnId, voiceId, {
          status: 'error',
          error: err?.message || 'Something went wrong',
        })
      } finally {
        delete abortRefs.current[refKey]
      }
    },
    [updateVoice, enqueueText, flushPendingText]
  )

  const startTurn = useCallback(
    async ({ message, voices, primaryId, lenses = [], anchor = null, attachments = [] }) => {
      if (!user) throw new Error('Not signed in')
      if (!message?.trim() || !voices?.length) return

      // Cancel only the latest turn's in-flight streams (let earlier turns
      // settle naturally — they shouldn't be running, but defensive).
      cancelAll()

      // crypto.randomUUID guarantees uniqueness; the older Date.now-based
      // id could collide if the user fired two turns in the same tick.
      const turnId = `local-${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`}`
      const newTurn = {
        id: turnId,
        // Filled in below once insertUserMessage resolves — edit-and-resend
        // reads this as the truncate anchor (T3-B). Null until the insert
        // returns; UI shouldn't enable Edit on a turn whose user row hasn't
        // landed in the DB yet.
        userMessageId: null,
        message,
        voices: [...voices],
        primaryId,
        lenses: [...lenses],
        anchor,
        voiceState: Object.fromEntries(
          voices.map(v => [
            v,
            {
              status: 'streaming',
              text: '',
              usedLenses: [],
              messageId: null,
              followUps: [],
              stopReason: null,
              disputedTopic: null,
            },
          ])
        ),
        synthesis: null,
        sentAt: Date.now(),
        // Attach the user's images to the live turn so the user pill can
        // render thumbnails immediately (before the conversation reloads).
        attachments: attachments?.length ? [...attachments] : null,
      }
      setTurns(prev => [...prev, newTurn])

      let convId = conversationId
      // Hoisted out of try so the streamOne fan-out below can pass it
      // (T2-A: edge function filters this id from history to avoid
      // sending the user's prompt to Claude twice).
      let insertedUserMessageId = null
      try {
        if (!convId) {
          convId = await createConversationV2({
            userId: user.id,
            tab: VOICE_TO_TAB[primaryId] || 'general',
            metadata: {
              mode: anchor ? 'passage_study' : 'free_chat',
              anchor: anchor || null,
              voices: [...voices],
              lenses_active: [...lenses],
            },
          })
          setConversationId(convId)
        }
        insertedUserMessageId = await insertUserMessage({
          conversationId: convId,
          content: message,
          // T2-B: persist attachments on the user row so they survive
          // reload / share / export. Live turn already carries them in
          // local state for immediate render.
          attachments: attachments?.length ? attachments : [],
          // Persist the turn's voice display order + which voice is primary
          // so reload rebuilds the same column layout regardless of which
          // assistant row landed first (chat audit T2-D).
          metadata: {
            voice_order: [...voices],
            primary_voice: primaryId,
          },
        })
        // Surface the DB user-message id on the live turn so edit-and-resend
        // can use it as the truncate anchor (T3-B), and so the fan-out
        // streamOne calls below can pass it for history dedup (T2-A).
        if (insertedUserMessageId) {
          setTurns(prev =>
            prev.map(t => (t.id === turnId ? { ...t, userMessageId: insertedUserMessageId } : t))
          )
        }
      } catch (err) {
        captureException(err, {
          tags: { feature: 'chat', op: 'start-turn' },
        })
        const errMsg = err?.message || 'Could not start the conversation.'
        setTurns(prev =>
          prev.map(t =>
            t.id === turnId
              ? {
                  ...t,
                  voiceState: Object.fromEntries(
                    voices.map(v => [
                      v,
                      { status: 'error', text: '', error: errMsg, usedLenses: [], messageId: null },
                    ])
                  ),
                }
              : t
          )
        )
        return
      }

      voices.forEach(voiceId => {
        streamOne({
          turnId,
          voiceId,
          isPrimary: voiceId === primaryId,
          conversationId: convId,
          message,
          lenses,
          anchor,
          attachments,
          userMessageId: insertedUserMessageId,
        })
      })
    },
    [cancelAll, conversationId, streamOne, user]
  )

  const regenerate = useCallback(
    voiceId => {
      const turn = turns[turns.length - 1]
      if (!turn || !conversationId) return
      if (!turn.voices.includes(voiceId)) return
      const refKey = `${turn.id}:${voiceId}`
      const ac = abortRefs.current[refKey]
      if (ac) {
        ac.abort()
        delete abortRefs.current[refKey]
      }
      setTurns(prev =>
        prev.map(t =>
          t.id === turn.id
            ? {
                ...t,
                voiceState: {
                  ...t.voiceState,
                  [voiceId]: {
                    status: 'streaming',
                    text: '',
                    usedLenses: [],
                    messageId: null,
                  },
                },
              }
            : t
        )
      )
      streamOne({
        turnId: turn.id,
        voiceId,
        isPrimary: voiceId === turn.primaryId,
        conversationId,
        message: turn.message,
        lenses: turn.lenses,
        anchor: turn.anchor,
        // T2-A: filter the original user row out of history when the
        // edge function builds claudeMessages — without this, regenerate
        // sends the prompt twice (once from history, once as the
        // appended user turn).
        userMessageId: turn.userMessageId,
      })
    },
    [turns, conversationId, streamOne]
  )

  // Continue-generating — when Claude stopped at max_tokens (stopReason set),
  // resend with `__continue__` + continueFromMessageId so the chat function
  // appends to the existing assistant row instead of inserting a new one.
  // Per-voice in multi-voice fan-out: each voice has its own messageId.
  const continueGenerating = useCallback(
    voiceId => {
      const turn = turns[turns.length - 1]
      if (!turn || !conversationId) return
      if (!turn.voices.includes(voiceId)) return
      const cur = turn.voiceState[voiceId]
      if (!cur || !cur.messageId) return
      const refKey = `${turn.id}:${voiceId}`
      const ac = abortRefs.current[refKey]
      if (ac) {
        ac.abort()
        delete abortRefs.current[refKey]
      }
      // Flip back to streaming and clear the stopReason marker so the
      // Continue button hides while we generate. Keep the existing text —
      // the chat function appends, and so do we.
      setTurns(prev =>
        prev.map(t =>
          t.id === turn.id
            ? {
                ...t,
                voiceState: {
                  ...t.voiceState,
                  [voiceId]: { ...cur, status: 'streaming', stopReason: null },
                },
              }
            : t
        )
      )
      streamOne({
        turnId: turn.id,
        voiceId,
        isPrimary: voiceId === turn.primaryId,
        conversationId,
        message: '__continue__',
        lenses: turn.lenses,
        anchor: turn.anchor,
        continueFromMessageId: cur.messageId,
      })
    },
    [turns, conversationId, streamOne]
  )

  // Truncate the local turn list at and after the given turnId. Used by
  // edit-and-resend — caller drops the user's stale turn (and any later
  // turns) from the UI, then dispatches a fresh startTurn with the edited
  // text. We don't delete the DB rows; the assistant rows for the dropped
  // turns become orphaned in `messages` until the user clears their
  // history. Cancels any in-flight streams since they belong to dropped
  // turns by definition.
  const truncateTurnsFrom = useCallback(
    turnId => {
      cancelAll()
      setTurns(prev => {
        const idx = prev.findIndex(t => t.id === turnId)
        if (idx < 0) return prev
        return prev.slice(0, idx)
      })
    },
    [cancelAll]
  )

  const loadConversation = useCallback(
    async id => {
      if (!id) return
      cancelAll()
      try {
        const { conversation, messages } = await fetchConversationFull(id)
        const restoredTurns = buildTurnsFromMessages(messages)
        setTurns(restoredTurns)
        setConversationId(conversation.id)
        setConversationMetadata(conversation.metadata || {})
      } catch (err) {
        captureException(err, {
          tags: { feature: 'chat', op: 'load-conversation' },
        })
        console.error('loadConversation failed:', err)
      }
    },
    [cancelAll]
  )

  const reset = useCallback(() => {
    cancelAll()
    setTurns([])
    setConversationId(null)
    setConversationMetadata({})
  }, [cancelAll])

  // Synthesis is set externally — when the synthesize hook lands a result,
  // its caller can call attachSynthesis to bake it into the latest turn.
  const attachSynthesis = useCallback(({ turnId, synthesis }) => {
    setTurns(prev => prev.map(t => (t.id === turnId ? { ...t, synthesis } : t)))
  }, [])

  const latestTurn = turns[turns.length - 1] ?? null
  const isStreaming = !!(
    latestTurn && Object.values(latestTurn.voiceState).some(v => v.status === 'streaming')
  )

  return {
    turns,
    latestTurn,
    conversationId,
    conversationMetadata,
    isStreaming,
    monthlyMessagesUsed,
    monthlyMessagesRemaining,
    startTurn,
    regenerate,
    continueGenerating,
    truncateTurnsFrom,
    loadConversation,
    cancelAll,
    reset,
    attachSynthesis,
  }
}
